if (!customElements.get('cro-complete-fit')) {
  customElements.define('cro-complete-fit', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.hasAttribute('data-test-only') && new URLSearchParams(location.search).get('cyf') !== '1') return;
      this.data = JSON.parse(this.querySelector('[data-fit-data]').textContent);
      this.dialog = this.querySelector('dialog');
      this.designs = this.querySelector('[data-designs]');
      this.sizes = this.querySelector('[data-sizes]');
      this.price = this.querySelector('[data-price]');
      this.error = this.querySelector('[data-error]');
      this.addButton = this.querySelector('[data-add]');
      this.hidden = false;
      this.querySelectorAll('[data-category]').forEach((button) => {
        button.addEventListener('click', () => this.open(button));
      });
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.trigger?.focus());
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const buttons = [...this.dialog.querySelectorAll('button:not(:disabled)')];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      this.addButton.addEventListener('click', () => this.add());
    }

    open(trigger) {
      this.trigger = trigger;
      this.category = this.data.categories.find((category) => category.id === Number(trigger.dataset.category));
      this.querySelector('[data-modal-title]').textContent = this.category.label;
      this.showError('');
      this.designs.replaceChildren();
      this.sizes.replaceChildren();
      this.price.replaceChildren();
      this.variant = null;
      this.addButton.disabled = true;
      this.category.products.forEach((product) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cro-fit__design';
        button.disabled = !product.variants.some((variant) => variant.available);
        button.setAttribute('aria-pressed', 'false');
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.width = 400;
          image.height = 400;
          image.loading = 'lazy';
          button.append(image);
        }
        const title = document.createElement('span');
        title.textContent = product.title + (button.disabled ? ' (Ausverkauft)' : '');
        button.append(title);
        button.addEventListener('click', () => this.selectProduct(product, button));
        this.designs.append(button);
      });
      const first = this.category.products.find((product) => product.variants.some((variant) => variant.available));
      if (first) this.selectProduct(first, this.designs.children[this.category.products.indexOf(first)]);
      this.dialog.showModal();
      this.querySelector('[data-close]').focus();
    }

    selectProduct(product, button) {
      this.designs.querySelectorAll('button').forEach((tile) => tile.setAttribute('aria-pressed', String(tile === button)));
      this.sizes.replaceChildren();
      this.variant = null;
      this.addButton.disabled = true;
      this.showError('');
      this.showPrice(product.variants.find((variant) => variant.available).price);
      product.variants.forEach((variant) => {
        const size = document.createElement('button');
        size.type = 'button';
        size.className = 'cro-fit__size';
        size.textContent = variant.title;
        size.disabled = !variant.available;
        size.setAttribute('aria-pressed', 'false');
        if (!variant.available) size.setAttribute('aria-label', `${variant.title} ausverkauft`);
        size.addEventListener('click', () => {
          this.variant = variant;
          this.sizes.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === size)));
          this.showPrice(variant.price);
          this.showError('');
          this.addButton.disabled = false;
        });
        this.sizes.append(size);
      });
    }

    // Same output as Liquid `| money` on the cards (shop money format, e.g. "€{{amount_with_comma_separator}}").
    formatMoney(cents) {
      return this.data.moneyFormat.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        const digits = key.includes('no_decimals') ? 0 : 2;
        return (cents / 100).toLocaleString(key.includes('comma') ? 'de-DE' : 'en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
      });
    }

    showPrice(cents) {
      const normal = document.createElement('s');
      normal.textContent = this.formatMoney(cents);
      normal.setAttribute('aria-label', `Normalpreis ${normal.textContent}`);
      const action = document.createElement('strong');
      action.textContent = this.formatMoney(Math.max(0, cents - this.category.discount));
      action.setAttribute('aria-label', `Aktionspreis ${action.textContent}`);
      this.price.replaceChildren(action, normal);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async add() {
      if (this.busy || !this.variant?.available) return;
      const form = document.getElementById(this.dataset.formId);
      const input = form?.matches('form[action*="/cart/add"]') ? form.querySelector('[name="id"]') : null;
      const tracksuit = this.data.tracksuitVariants.find((variant) => String(variant.id) === input?.value);
      const submit = form?.querySelector('[type="submit"][name="add"]');
      if (!input || input.disabled || !tracksuit?.available || submit?.disabled || submit?.getAttribute('aria-disabled') === 'true') {
        this.showError('Bitte wähle zuerst deine Tracksuit Größe.');
        return;
      }
      this.busy = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.classList.add('loading');
      this.addButton.querySelector('.loading__spinner').classList.remove('hidden');
      this.showError('');
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const canRender = cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function';
      const body = { items: [{ id: tracksuit.id, quantity: 1 }, { id: this.variant.id, quantity: 1 }] };
      try {
        if (canRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(this.trigger);
        }
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.showError(result.description || result.message || 'Die Produkte konnten nicht hinzugefügt werden. Bitte versuche es erneut.');
          return;
        }
        this.dialog.close();
        // Multi-item adds nest the key/id that the theme expects at the top level.
        const state = { ...result, ...result.items?.[0], sections: result.sections };
        if (!canRender || !state.sections || Object.values(state.sections).some((section) => !section)) {
          window.location.assign(`${root}cart`);
          return;
        }
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cro-complete-fit', productVariantId: tracksuit.id, cartData: state });
          }
          cart.classList.remove('is-empty');
          cart.renderContents(state);
        } catch {
          window.location.assign(`${root}cart`);
        }
      } catch {
        this.showError('Die Produkte konnten nicht hinzugefügt werden. Bitte versuche es erneut.');
      } finally {
        this.busy = false;
        this.addButton.disabled = false;
        this.addButton.removeAttribute('aria-busy');
        this.addButton.classList.remove('loading');
        this.addButton.querySelector('.loading__spinner').classList.add('hidden');
      }
    }
  });
}
